import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { Loader2, Sparkles, Plus, X, ListChecks, BarChart3, RefreshCw, Camera, HandCoins, Eye, TrendingUp } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { NuevoInformeModal } from "./InformesTab"; // Fix CJ (2026-09-11)
import { Skeleton } from "../../components/ui/skeleton";
import MontoInput from "../../components/MontoInput"; // Fix CO (2026-09-13)
import ContratoTerceroModal from "./ContratoTerceroModal"; // Fix CO (2026-09-13)
import { toast } from "sonner";
import CurvaS from "./CurvaS"; // Fix IJ (2026-10-30)

const opts = { withCredentials: true };
const ESTADO_META = {
  no_iniciado: { label: "No iniciado", cls: "bg-gray-100 text-gray-500" },
  en_proceso: { label: "En proceso", cls: "bg-amber-100 text-amber-700" },
  finalizado: { label: "Finalizado", cls: "bg-emerald-100 text-emerald-700" },
};

// Fix AV (2026-09-06): "en la casilla donde figura el total cobrado, al
// seleccionar tener el desglose de historial de pagos realizados al contratista,
// estos pagos deben tener asiento bancario" (Edson, 05/09).
function PagosTercerizadoModal({ obraId, tarea, onClose, onSaved }) {
  const [form, setForm] = useState({ fecha: "", monto: "", titular: "", banco: "", cuenta: "", comprobante: "", factura: "", observaciones: "" });
  const [guardando, setGuardando] = useState(false);
  const pagos = tarea.pagos_tercerizado || [];
  const cobrado = pagos.reduce((s, p) => s + (Number(p.monto) || 0), 0);
  const cerrado = Number(tarea.monto_cerrado_tercerizado) || 0;

  const agregar = async () => {
    if (!form.fecha) { toast.error("Poné la fecha del pago"); return; }
    if (!(Number(form.monto) > 0)) { toast.error("El monto tiene que ser mayor a cero"); return; }
    setGuardando(true);
    try {
      const { data } = await axios.post(`${API}/obras/${obraId}/tareas/${tarea.id}/pagos`, {
        ...form, monto: Number(form.monto),
      }, { withCredentials: true });
      toast.success("Pago registrado");
      setForm({ fecha: "", monto: "", titular: "", banco: "", cuenta: "", comprobante: "", factura: "", observaciones: "" });
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar el pago");
    } finally { setGuardando(false); }
  };

  const borrar = async (pagoId) => {
    if (!window.confirm("¿Borrar este pago del historial?")) return;
    try {
      const { data } = await axios.delete(`${API}/obras/${obraId}/tareas/${tarea.id}/pagos/${pagoId}`, { withCredentials: true });
      toast.success("Pago borrado");
      onSaved?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo borrar"); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4" data-testid="pagos-tercerizado-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Pagos al contratista</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">{tarea.tarea_nombre} · {tarea.contratista || "sin contratista cargado"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded-xl p-2">
            <p className="text-[10px] text-gray-500 uppercase">Monto cerrado</p>
            <p className="text-sm font-bold text-gray-800">{formatPYG(cerrado)}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-2">
            <p className="text-[10px] text-emerald-600 uppercase">Cobrado</p>
            <p className="text-sm font-bold text-emerald-700">{formatPYG(cobrado)}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-2">
            <p className="text-[10px] text-amber-600 uppercase">Falta pagar</p>
            <p className="text-sm font-bold text-amber-700">{formatPYG(cerrado - cobrado)}</p>
          </div>
        </div>

        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-[#EBF3FB]">
              <tr>
                {["Fecha", "Monto", "Titular / banco", "Cuenta", "Comprobante", "Factura", ""].map(h => (
                  <th key={h} className="text-left px-2 py-1.5 font-bold text-[#1A4B7A] uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagos.map(p => (
                <tr key={p.id} className="border-t border-gray-100" data-testid={`pago-tercerizado-${p.id}`}>
                  <td className="px-2 py-1.5">{p.fecha}</td>
                  <td className="px-2 py-1.5 font-semibold text-right">{formatPYG(p.monto)}</td>
                  <td className="px-2 py-1.5">{p.titular || "—"}{p.banco ? ` · ${p.banco}` : ""}</td>
                  <td className="px-2 py-1.5">{p.cuenta || "—"}</td>
                  <td className="px-2 py-1.5">{p.comprobante || "—"}</td>
                  <td className="px-2 py-1.5">{p.factura || "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => borrar(p.id)} className="text-red-400 hover:text-red-600">✕</button>
                  </td>
                </tr>
              ))}
              {pagos.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-4 text-center text-gray-400">Todavía no hay pagos registrados.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div>
          <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide mb-2">Registrar un pago</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-fecha" />
            <MontoInput value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))}
              placeholder="Monto" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-monto" />
            <input value={form.titular} onChange={e => setForm(f => ({ ...f, titular: e.target.value }))}
              placeholder="Titular de la cuenta" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-titular" />
            <input value={form.banco} onChange={e => setForm(f => ({ ...f, banco: e.target.value }))}
              placeholder="Banco" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-banco" />
            <input value={form.cuenta} onChange={e => setForm(f => ({ ...f, cuenta: e.target.value }))}
              placeholder="N° de cuenta" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-cuenta" />
            <input value={form.comprobante} onChange={e => setForm(f => ({ ...f, comprobante: e.target.value }))}
              placeholder="N° de comprobante" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-comprobante" />
            <input value={form.factura} onChange={e => setForm(f => ({ ...f, factura: e.target.value }))}
              placeholder="N° de factura" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="pago-factura" />
            <input value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
              placeholder="Observaciones" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">Cerrar</button>
            <button onClick={agregar} disabled={guardando}
              className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-bold" data-testid="pago-guardar">
              {guardando ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Registrar pago
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AddTareaModal({ itemUid, itemLabel, onClose, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [pct, setPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!nombre.trim()) { toast.error("Ingresá el nombre de la tarea"); return; }
    setSaving(true);
    try {
      await onSaved({ item_uid: itemUid, tarea_nombre: nombre.trim(), pct_item: Number(pct) || 0 });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear la tarea"); }
    finally { setSaving(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="tareas-add-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Nueva tarea — {itemLabel}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Nombre de la tarea</label>
            <input value={nombre} onChange={e => setNombre(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm mt-1" data-testid="tareas-add-nombre-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">% del fondo M.O. del ítem</label>
            <input type="number" min={0} max={100} value={pct} onChange={e => setPct(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm mt-1" data-testid="tareas-add-pct-input" />
          </div>
        </div>
        <button onClick={submit} disabled={saving} className="w-full mt-5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2" data-testid="tareas-add-save-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear tarea
        </button>
      </div>
    </div>, document.body
  );
}

// Fix N (2026-08-31): los TRES cronogramas que pidió el cliente el 24/08:
// el del presupuesto (línea base), el del residente y el realmente ejecutado.
// Antes se dibujaban dos. Una tarea entra al gráfico si tiene fechas en
// cualquiera de los tres, no solo en `plan` — antes una tarea con línea base
// pero sin plan del residente quedaba invisible.
// Fix BM (2026-09-08): "Los filtros pueden ser multiselección tipo el
// googlesheets?" (Edson, 08/09). Mismo comportamiento que la planilla: casillas,
// buscador adentro, "Seleccionar todo" y "Borrar", contador, y Aceptar/Cancelar.
// Nada se filtra hasta Aceptar, así que se puede marcar y desmarcar tranquilo.
// Una selección vacía significa "todos", igual que en la planilla.
// Fix BQ (2026-09-08): se exporta tal cual para reusarlo en Ejecución en Campo.
// No cambia nada de su comportamiento: la pestaña Tareas lo sigue usando igual.
export function FiltroMulti({ etiqueta, opciones, seleccion, onAplicar, testId }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [borrador, setBorrador] = useState(new Set());
  // Fix IJ (2026-10-30): "en el filtro, pueden aparecer primero los seleccionados" (CEN). El orden se fija al abrir,
  // para que la lista no salte mientras se marca.
  const [orden, setOrden] = useState(null);

  const abrir = () => {
    setBorrador(new Set(seleccion.size ? seleccion : opciones.map(o => o.value)));
    setOrden([...opciones.filter(o => seleccion.has(o.value)), ...opciones.filter(o => !seleccion.has(o.value))]);
    setQ("");
    setAbierto(true);
  };

  const visibles = (orden || opciones).filter(o => !q || (o.label || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => setBorrador(prev => {
    const n = new Set(prev);
    if (n.has(v)) n.delete(v); else n.add(v);
    return n;
  });

  const aceptar = () => {
    // Todo marcado es lo mismo que no filtrar.
    onAplicar(borrador.size === opciones.length ? new Set() : new Set(borrador));
    setAbierto(false);
  };

  const resumen = seleccion.size === 0
    ? etiqueta
    : seleccion.size === 1
      ? ((opciones.find(o => seleccion.has(o.value)) || {}).label || etiqueta)
      : `${etiqueta}: ${seleccion.size}`;

  return (
    <div className="relative">
      <button type="button" onClick={() => (abierto ? setAbierto(false) : abrir())}
        className={`border rounded-lg px-2 py-1.5 text-xs bg-white flex items-center gap-1.5 max-w-[210px] ${seleccion.size ? "border-[#EA580C] text-[#C2410C] font-semibold" : "border-gray-200 text-gray-600"}`}
        data-testid={testId}>
        <span className="truncate">{resumen}</span>
        <span className="text-gray-400">▾</span>
      </button>
      {abierto && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setAbierto(false)} />
          <div className="absolute z-50 mt-1 left-0 w-72 bg-white border border-gray-200 rounded-xl shadow-xl p-2" data-testid={`${testId}-panel`}>
            <div className="flex items-center justify-between text-[11px] mb-1.5 px-1">
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setBorrador(new Set(visibles.map(o => o.value)))}
                  className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`${testId}-todos`}>
                  Seleccionar todo {visibles.length}
                </button>
                <span className="text-gray-300">·</span>
                <button type="button" onClick={() => setBorrador(new Set())}
                  className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`${testId}-borrar`}>
                  Borrar
                </button>
              </div>
              <span className="text-gray-400">Mostrando {borrador.size}</span>
            </div>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar…"
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs mb-1.5"
              data-testid={`${testId}-buscar`} />
            <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
              {visibles.length === 0 && <p className="text-[11px] text-gray-400 px-1 py-3 text-center">Sin resultados</p>}
              {visibles.map(o => (
                <label key={o.value} className="flex items-center gap-2 px-1 py-1.5 text-xs text-gray-700 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={borrador.has(o.value)} onChange={() => toggle(o.value)}
                    className="w-3.5 h-3.5 accent-[#EA580C]" />
                  <span className="truncate" title={o.label}>{o.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setAbierto(false)}
                className="flex-1 border border-gray-300 text-gray-600 rounded-lg py-1.5 text-xs font-medium hover:bg-gray-50">
                Cancelar
              </button>
              <button type="button" onClick={aceptar}
                className="flex-1 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg py-1.5 text-xs font-semibold"
                data-testid={`${testId}-aceptar`}>
                Aceptar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Fix IJ (2026-10-30): "El gantt que se vea así (meses y semanas)", "color distinto por tarea" y "cuando tenemos una
// tarea repetida en un nivel, que se vea unificado en el gantt, en una sola fila" (CEN). Cada fila es una tarea en un
// nivel; sus tramos son el plan calendarizado (claro si solo hay el según presupuesto) y lo ejecutado (línea verde).
const MESES_GANTT = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const PALETA_GANTT = ["#2E6DA4", "#EA580C", "#16A34A", "#9333EA", "#DC2626", "#0891B2", "#CA8A04", "#DB2777", "#4F46E5", "#059669",
  "#B45309", "#7C3AED", "#0EA5E9", "#65A30D", "#E11D48", "#0D9488", "#A16207", "#C026D3", "#1D4ED8", "#475569"];
const DIA_MS = 86400000;
const aDiaUTC = (s) => { const [y, m, d] = String(s).slice(0, 10).split("-").map(Number); return Date.UTC(y, m - 1, d); };

function GanttChart({ tareas }) {
  const rango = (r) => (r?.inicio && r?.fin && aDiaUTC(r.fin) >= aDiaUTC(r.inicio) ? [aDiaUTC(r.inicio), aDiaUTC(r.fin)] : null);
  const grupos = new Map();
  tareas.forEach(t => {
    const plan = rango(t.plan), base = plan ? null : rango(t.plan_presupuesto);
    const real = t.real?.inicio ? rango({ inicio: t.real.inicio, fin: t.real.fin || t.plan?.fin || t.real.inicio }) : null;
    if (!plan && !base && !real) return;
    const k = `${t.tarea_nombre}||${t.nivel_label || ""}`;
    if (!grupos.has(k)) grupos.set(k, { k, nombre: t.tarea_nombre, nivel: t.nivel_label || "", tramos: [] });
    grupos.get(k).tramos.push({ plan, base, real });
  });
  if (!grupos.size) return <p className="text-xs text-gray-400 italic py-4 text-center">Sin fechas cargadas todavía.</p>;
  const filas = [...grupos.values()].map(g => ({ ...g, desde: Math.min(...g.tramos.flatMap(x => [x.plan, x.base, x.real].filter(Boolean).map(r => r[0]))) }))
    .sort((a, b) => a.desde - b.desde || a.nombre.localeCompare(b.nombre, "es"));
  const todos = filas.flatMap(g => g.tramos.flatMap(x => [x.plan, x.base, x.real].filter(Boolean).flat()));
  const d0 = new Date(Math.min(...todos)), d1 = new Date(Math.max(...todos));
  const ini = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth(), 1), fin = Date.UTC(d1.getUTCFullYear(), d1.getUTCMonth() + 1, 0);
  const total = Math.round((fin - ini) / DIA_MS) + 1;
  const izq = (d) => `${((d - ini) / DIA_MS / total) * 100}%`;
  const ancho = (a, b) => `${Math.max((((b - a) / DIA_MS + 1) / total) * 100, 0.4)}%`;
  const meses = [];
  for (let y = d0.getUTCFullYear(), m = d0.getUTCMonth(); Date.UTC(y, m, 1) <= fin; m === 11 ? (y += 1, m = 0) : (m += 1)) {
    const ultimo = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    meses.push({ clave: `${y}-${String(m + 1).padStart(2, "0")}`, nombre: `${MESES_GANTT[m]} ${y}`, a: Date.UTC(y, m, 1), b: Date.UTC(y, m, ultimo),
      semanas: [1, 8, 15, 22, 29].filter(d => d <= ultimo).map((d, i) => ({ s: `S${i + 1}`, a: Date.UTC(y, m, d), b: Date.UTC(y, m, Math.min(d + 6, ultimo)) })) });
  }
  const nombres = [...new Set(filas.map(g => g.nombre))].sort((a, b) => a.localeCompare(b, "es"));
  const color = (n) => PALETA_GANTT[nombres.indexOf(n) % PALETA_GANTT.length];
  return (
    <div className="overflow-x-auto" data-testid="tareas-gantt">
      <div style={{ minWidth: Math.max(720, total * 5) + 360 }}>
        {/* Fix IN (2026-10-30): "necesito que en esa columna se visualice el texto completo de la tarea, así como también el
            nivel, el nivel puede ser en una columnita al lado" (CEN). La tarea se lee entera (en varias líneas si hace falta)
            y el nivel va en su propia columna. */}
        <div className="flex text-[10px] font-semibold text-gray-600">
          <div className="w-[230px] flex-shrink-0 self-end pb-1 uppercase text-gray-400">Tarea</div>
          <div className="w-[130px] flex-shrink-0 self-end pb-1 uppercase text-gray-400">Nivel</div>
          <div className="relative flex-1 h-6 border-b border-gray-200">
            {meses.map(m => (
              <div key={m.clave} className="absolute top-0 h-6 px-1 border-l border-gray-300 truncate" style={{ left: izq(m.a), width: ancho(m.a, m.b) }} data-testid={`tareas-gantt-mes-${m.clave}`}>{m.nombre}</div>
            ))}
          </div>
        </div>
        <div className="flex text-[9px] text-gray-400 mb-1">
          <div className="w-[360px] flex-shrink-0" />
          <div className="relative flex-1 h-5 border-b border-gray-100">
            {meses.flatMap(m => m.semanas.map(w => (
              <div key={`${m.clave}-${w.s}`} className="absolute top-0 h-5 pl-0.5 border-l border-gray-100" style={{ left: izq(w.a), width: ancho(w.a, w.b) }} data-testid="tareas-gantt-semana">{w.s}</div>
            )))}
          </div>
        </div>
        {filas.map((g, i) => (
          <div key={g.k} className="flex items-center text-xs hover:bg-gray-50/60" data-testid={`tareas-gantt-fila-${i}`} data-tramos={g.tramos.length}>
            <div className="w-[230px] flex-shrink-0 pr-2 py-1 flex items-start gap-1.5 break-words" data-testid={`tareas-gantt-tarea-${i}`}>
              <span className="inline-block w-2.5 h-2.5 rounded-sm mt-0.5 flex-shrink-0" style={{ background: color(g.nombre) }} />
              <span className="text-gray-700 leading-snug min-w-0">{g.nombre}{g.tramos.length > 1 && <span className="text-gray-400"> ×{g.tramos.length}</span>}</span>
            </div>
            <div className="w-[130px] flex-shrink-0 pr-2 py-1 text-gray-500 leading-snug break-words" data-testid={`tareas-gantt-nivel-${i}`}>{g.nivel || "—"}</div>
            <div className="relative flex-1 self-stretch min-h-[28px]">{/* Fix IN: la fila crece con el texto; las barras quedan al medio */}
              {meses.map(m => <div key={m.clave} className="absolute top-0 bottom-0 border-l border-gray-100" style={{ left: izq(m.a) }} />)}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-7">
              {g.tramos.map((x, j) => (
                <Fragment key={j}>
                  {(x.plan || x.base) && (
                    <div className="absolute top-1.5 h-3.5 rounded-full" data-testid="tareas-gantt-barra"
                      style={{ left: izq((x.plan || x.base)[0]), width: ancho(...(x.plan || x.base)), background: color(g.nombre), opacity: x.plan ? 1 : 0.35 }}
                      title={x.plan ? "Plan calendarizado" : "Según presupuesto"} />
                  )}
                  {x.real && <div className="absolute bottom-0.5 h-1 rounded-full bg-emerald-700" style={{ left: izq(x.real[0]), width: ancho(...x.real) }} title="Ejecutado" />}
                </Fragment>
              ))}
              </div>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-4 text-[10px] text-gray-400 pt-2 pl-[360px]">
          <span>Color por tarea · claro: según presupuesto, sin calendarizar</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1 bg-emerald-700 rounded-full inline-block" /> Ejecutado</span>
        </div>
      </div>
    </div>
  );
}

// Fix CJ (2026-09-11): "link desde la tarea al informe" (Edson, 24/08): los informes con foto
// de la tarea y un botón para cargar uno nuevo ya enlazado a ella.
function InformesTareaModal({ obraId, tarea, informes, tareas, onClose, onSaved }) {
  const [nuevo, setNuevo] = useState(false);
  return createPortal(
    <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4" data-testid="tareas-informes-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="font-bold text-gray-900">Informes de la tarea</p>
            <p className="text-xs text-gray-500">{tarea.item_numero} — {tarea.tarea_nombre}{tarea.nivel_label ? ` · ${tarea.nivel_label}` : ""}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {informes.length === 0 ? <p className="text-sm text-gray-400 text-center py-6">Esta tarea todavía no tiene informes.</p> : informes.map(inf => (
            <div key={inf.id} className="border border-gray-100 rounded-xl p-3" data-testid={`tareas-informe-${inf.id}`}>
              <p className="font-semibold text-gray-900 text-sm">{inf.titulo}</p>
              <p className="text-[11px] text-gray-400">{formatDate(inf.fecha)} · {inf.created_by_name}</p>
              {inf.descripcion && <p className="text-sm text-gray-600 mt-1">{inf.descripcion}</p>}
              {inf.fotos?.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {inf.fotos.map((f, i) => (
                    <a key={i} href={`${API}/obras/${obraId}/informes/${inf.id}/fotos/${i}`} target="_blank" rel="noreferrer">
                      <img src={`${API}/obras/${obraId}/informes/${inf.id}/fotos/${i}`} alt={f.filename} className="w-20 h-20 object-cover rounded-lg border border-gray-100" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={() => setNuevo(true)} className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="tareas-informe-nuevo-btn">
            <Plus size={13} /> Nuevo informe de esta tarea
          </button>
        </div>
      </div>
      {nuevo && <NuevoInformeModal obraId={obraId} tareas={tareas} tareaInicial={tarea.id} onClose={() => setNuevo(false)} onSaved={onSaved} />}
    </div>,
    document.body
  );
}

export default function TareasTab({ obraId, canFullAccess, isResidente, vistaCliente }) {
  // Fix IF (2026-10-29): "cuando se está en modo visualización al cliente que no muestre ningún
  // fondo o costo de MO" (CEN). `gestion` es lo que antes era `canFullAccess` para fondos, costos
  // y acciones de gestión; en la vista del cliente se apaga y aparece el precio de venta.
  const gestion = canFullAccess && !vistaCliente;
  const [tareas, setTareas] = useState([]);
  const [niveles, setNiveles] = useState([]);
  // Fix BM (2026-09-08): el resumen del servidor (fondo_filtrado_*) se calculaba
  // sobre su propio filtro; ahora el fondo sale de lo que se está viendo, así que
  // este estado deja de tener uso.
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [resincronizando, setResincronizando] = useState(false);
  // Fix BM (2026-09-08): multiselección. Un conjunto vacío significa "todos".
  // Fix BZ (2026-09-09): Secciones se suma a las tres multiselecciones del Fix BM
  // —es la que ya tiene Ejecución en Campo— y la caja de texto replica el
  // "Filtrar por ítem o sección" del desplegable del pedido de materiales.
  const [filtroSecciones, setFiltroSecciones] = useState(new Set());
  const [busqueda, setBusqueda] = useState("");
  // Cambios cargados en el presupuesto que esta obra todavía no tomó. Solo se
  // muestra: no dispara ninguna actualización por su cuenta.
  const [pendienteSync, setPendienteSync] = useState(null);
  const [filtroNiveles, setFiltroNiveles] = useState(new Set());
  const [filtroTareas, setFiltroTareas] = useState(new Set());
  const [filtroEstados, setFiltroEstados] = useState(new Set());
  const [addModalItem, setAddModalItem] = useState(null);
  // Fix AV (2026-09-06): historial de pagos al contratista de una tarea.
  const [pagosTarea, setPagosTarea] = useState(null);
  // Fix CO (2026-09-13): "Desde acá tambien seria bueno que se pueda generar
  // contratos con tercerizados" (Edson, 13/09).
  const [showContrato, setShowContrato] = useState(false);
  // Fix EC (2026-09-24): filtro por presupuesto de origen y aviso de ítems sin tareas.
  const [filtroOrigen, setFiltroOrigen] = useState(new Set());
  const [sinTareas, setSinTareas] = useState([]);
  // Fix AW (2026-09-06): "la columna Fin debe completarse de forma automatica,
  // por defecto no debe contemplar los días domingos y fechas festivas" (Edson,
  // 05/09). Cuando el tramo pisa domingos o feriados se pregunta antes de guardar.
  const [confirmFin, setConfirmFin] = useState(null);

  const calcularFin = useCallback(async (tarea, inicio, dias, forzar) => {
    if (!inicio || !dias) return;
    try {
      const { data } = await axios.post(`${API}/calendario/fin`, {
        inicio, dias: Number(dias),
        trabaja_domingos: !!forzar, trabaja_feriados: !!forzar,
      }, opts);
      if (!forzar && (data.dias_no_laborables || []).length > 0) {
        setConfirmFin({ tarea, inicio, dias: Number(dias), data });
        return;
      }
      await guardarPlan(tarea, { ...tarea.plan, inicio, duracion_dias: Number(dias), fin: data.fin });   // Fix IJ
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo calcular la fecha de fin");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [showGantt, setShowGantt] = useState(false);
  const [showCurva, setShowCurva] = useState(false);   // Fix IJ: Curva S
  // Fix IJ (2026-10-30): "copiar el período de una tarea y poder pegar a otras tareas" (CEN) y la consulta de aplicar el
  // período a la misma tarea en el mismo nivel.
  const [periodoCopiado, setPeriodoCopiado] = useState(null);
  const [confirmarPegarTodo, setConfirmarPegarTodo] = useState(false);
  const [mismoNivel, setMismoNivel] = useState(null);
  const [showResumen, setShowResumen] = useState(false);
  const [resumen, setResumen] = useState(null);
  // Fix CJ (2026-09-11): informes con foto de cada tarea.
  const [informes, setInformes] = useState([]);
  const [informesTarea, setInformesTarea] = useState(null);
  const cargarInformes = useCallback(() => {
    axios.get(`${API}/obras/${obraId}/informes`, opts)
      .then(({ data }) => setInformes(data.informes || []))
      .catch(() => {});
  }, [obraId]);
  useEffect(() => { cargarInformes(); }, [cargarInformes]);
  const informesPorTarea = useMemo(() => {
    const m = {};
    informes.forEach(i => { if (i.tarea_id) (m[i.tarea_id] = m[i.tarea_id] || []).push(i); });
    return m;
  }, [informes]);

  const load = useCallback(() => {
    setLoading(true);
    // Fix BM (2026-09-08): se traen todas y el filtro se aplica en la pantalla.
    // Con multiselección, pedirlas al servidor en cada clic sería una ida y vuelta
    // por cada casilla.
    axios.get(`${API}/obras/${obraId}/tareas`, opts)
      .then(({ data }) => { setTareas(data.tareas || []); setNiveles(data.niveles_catalogo || []); setSinTareas(data.items_sin_tareas || []); })
      .catch(() => toast.error("Error cargando tareas"))
      .finally(() => setLoading(false));
    // Fix BZ (2026-09-09): el aviso de cambios pendientes va en una llamada
    // aparte y sin toast de error: si falla, la pestaña funciona igual, solo no
    // muestra el aviso. `resincronizar` termina llamando a `load`, así que el
    // aviso se recalcula solo después de traer los cambios.
    axios.get(`${API}/obras/${obraId}/resincronizar-presupuesto/pendiente`, opts)
      .then(({ data }) => setPendienteSync(data || null))
      .catch(() => setPendienteSync(null));
  }, [obraId]);
  useEffect(load, [load]);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await axios.post(`${API}/obras/${obraId}/tareas/generar`, {}, opts);
      toast.success(`${data.tareas_creadas} tarea(s) generada(s) desde el presupuesto`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al generar tareas"); }
    finally { setGenerando(false); }
  };

  // Fix Q (2026-08-31): trae desde el presupuesto los 3 campos que pueda
  // haberse cargado después de aprobarlo (tareas, nivel, sector), sin tocar
  // avance ni montos. Si actualizó algo y ya había tareas generadas, ofrece
  // borrarlas y regenerarlas para que tomen el desglose nuevo.
  const resincronizar = async () => {
    if (!window.confirm("Trae a esta obra el desglose de tareas, los niveles y los sectores que se hayan cargado en el presupuesto después de aprobarlo. No modifica el avance cargado ni los montos.")) return;
    setResincronizando(true);
    try {
      const { data } = await axios.post(`${API}/obras/${obraId}/resincronizar-presupuesto`, {}, opts);
      // Fix BF (2026-09-08): además de los tres campos de siempre, ahora la obra
      // toma las copias por nivel de los ítems que se distribuyeron.
      toast.success(
        `Ítems actualizados: ${data.actualizados}. Con desglose: ${data.items_con_desglose} de ${data.total_items}.`
        + (data.expandidos ? ` ${data.expandidos} ítem(s) repartido(s) en ${data.clones_creados} línea(s) por nivel.` : "")
      );
      // Fix BH (2026-09-08): ítems con varias líneas iguales en la obra que no se
      // pueden emparejar solos con las copias por nivel.
      if (data.sin_resolver_por_ambiguedad > 0) {
        toast.warning(`${data.sin_resolver_por_ambiguedad} ítem(s) tienen varias líneas iguales en la obra y hay que asignarles el nivel a mano.`);
      }
      if (data.tareas_huerfanas_con_datos > 0) {
        toast.warning(`${data.tareas_huerfanas_con_datos} tarea(s) del ítem anterior tienen avance o pagos cargados y se dejaron como estaban: revisalas.`);
      }
      if ((data.actualizados > 0 || data.expandidos > 0) && tareas.length > 0) {
        if (window.confirm("Hay tareas ya generadas en esta obra. Para que tomen el desglose nuevo hay que borrarlas y regenerarlas. ¿Regenerar ahora?")) {
          try {
            await axios.delete(`${API}/obras/${obraId}/tareas`, opts);
            const { data: genData } = await axios.post(`${API}/obras/${obraId}/tareas/generar`, {}, opts);
            toast.success(`${genData.tareas_creadas} tarea(s) generada(s) desde el presupuesto`);
          } catch (e) { toast.error(e.response?.data?.detail || "Error al regenerar las tareas"); }
        }
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al re-sincronizar con el presupuesto"); }
    finally { setResincronizando(false); }
  };

  const openResumen = async () => {
    setShowResumen(true);
    try {
      const { data } = await axios.get(`${API}/obras/${obraId}/tareas/resumen`, opts);
      setResumen(data);
    } catch { toast.error("Error cargando el resumen de avance"); }
  };

  const patchTarea = async (id, patch) => {
    try {
      const { data } = await axios.put(`${API}/obras/${obraId}/tareas/${id}`, patch, opts);
      setTareas(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
      // Fix CJ (2026-09-11): la cuadrilla por encima del 100% se avisa (no se bloquea).
      if (patch.cuadrilla && Number(data?.pct_uso_cuadrilla) > 100) {
        toast.warning(`La cuadrilla estimada usa el ${data.pct_uso_cuadrilla}% del fondo de mano de obra de la tarea`);
      }
      return data || {};   // Fix IJ: para saber si se guardó
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); return null; }
  };

  // Fix IJ (2026-10-30): "que el sistema consulte cuando calendarizamos una tarea, si deseamos aplicar el periodo a las
  // demás tareas iguales ... solamente a la misma tarea y si se encuentra en el mismo nivel" (CEN).
  const tareasRef = useRef(tareas);
  tareasRef.current = tareas;
  const guardarPlan = async (tarea, plan) => {
    const ok = await patchTarea(tarea.id, { plan });
    if (!ok || !plan.inicio || !plan.fin || !tarea.nivel_label) return;
    const otras = tareasRef.current.filter(x => x.id !== tarea.id && x.tarea_nombre === tarea.tarea_nombre && x.nivel_label === tarea.nivel_label
      && !(x.plan?.inicio === plan.inicio && x.plan?.fin === plan.fin));
    if (otras.length) setMismoNivel({ tarea, plan, otras });
  };
  const aplicarPeriodo = async (lista, p) => {
    let n = 0;
    for (const x of lista) {
      if (await patchTarea(x.id, { plan: { ...x.plan, inicio: p.inicio, duracion_dias: p.duracion_dias ?? null, fin: p.fin } })) n += 1;
    }
    return n;
  };
  const copiarPeriodo = (t) => {
    setPeriodoCopiado({ id: t.id, desde: `${t.item_numero} · ${t.tarea_nombre}`, inicio: t.plan.inicio, fin: t.plan.fin, duracion_dias: t.plan.duracion_dias ?? null });
    setConfirmarPegarTodo(false);
    toast.success("Período copiado");
  };
  const pegarPeriodo = async (lista) => {
    const n = await aplicarPeriodo(lista, periodoCopiado);
    if (n) toast.success(`Período pegado en ${n} tarea(s)`);
  };

  const addTarea = async (payload) => {
    await axios.post(`${API}/obras/${obraId}/tareas`, payload, opts);
    toast.success("Tarea creada");
    load();
  };

  // Fix BM (2026-09-08): las opciones salen de las tareas que hay, para que nadie
  // vea un filtro que no lleva a ningún lado.
  const opcionesNivel = useMemo(() => {
    const vistos = new Map();
    tareas.forEach(t => {
      const v = t.nivel_id || "";
      if (!vistos.has(v)) {
        vistos.set(v, v ? (t.nivel_label || (niveles.find(n => n.id === v) || {}).label || v) : "Sin nivel");
      }
    });
    return [...vistos.entries()].map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || "", "es"));
  }, [tareas, niveles]);

  const opcionesTarea = useMemo(() => (
    [...new Set(tareas.map(t => t.tarea_nombre || "(sin nombre)"))]
      .sort((a, b) => a.localeCompare(b, "es"))
      .map(v => ({ value: v, label: v }))
  ), [tareas]);

  const opcionesEstado = [
    { value: "no_iniciado", label: "No iniciado" },
    { value: "en_proceso", label: "En proceso" },
    { value: "finalizado", label: "Finalizado" },
  ];

  // Fix BZ (2026-09-09): las secciones salen de las tareas que hay, igual que el
  // resto de los filtros del Fix BM. El orden es numérico ("3.9" antes que
  // "3.29"), que es el del cómputo y no el alfabético.
  const opcionesSeccion = useMemo(() => (
    [...new Set(tareas.map(t => t.item_seccion || ""))]
      .sort((a, b) => (a || "Sin sección").localeCompare(b || "Sin sección", "es", { numeric: true }))
      .map(v => ({ value: v, label: v || "Sin sección" }))
  ), [tareas]);

  // Fix EC (2026-09-24): "Base" primero y los adicionales en orden.
  const opcionesOrigen = useMemo(() => (
    [...new Set(tareas.map(t => t.item_origen || "Base"))]
      .sort((a, b) => (a === "Base" ? -1 : b === "Base" ? 1 : a.localeCompare(b, "es", { numeric: true })))
      .map(v => ({ value: v, label: v }))
  ), [tareas]);

  const tareasFiltradas = useMemo(() => {
    // Fix BZ (2026-09-09): mismo criterio de búsqueda que el desplegable del
    // pedido de materiales (número y descripción del ítem, y sección), más el
    // nombre de la tarea y el nivel, que es lo que se busca en esta pantalla.
    const term = busqueda.trim().toLowerCase();
    return tareas.filter(t =>
      (filtroOrigen.size === 0 || filtroOrigen.has(t.item_origen || "Base"))
      && (filtroSecciones.size === 0 || filtroSecciones.has(t.item_seccion || ""))
      && (filtroNiveles.size === 0 || filtroNiveles.has(t.nivel_id || ""))
      && (filtroTareas.size === 0 || filtroTareas.has(t.tarea_nombre || "(sin nombre)"))
      && (filtroEstados.size === 0 || filtroEstados.has(t.estado || "no_iniciado"))
      && (!term || `${t.item_numero || ""} ${t.item_descripcion || ""} ${t.tarea_nombre || ""} ${t.nivel_label || ""} ${t.item_seccion || ""}`
        .toLowerCase().includes(term))
    );
  }, [tareas, filtroOrigen, filtroSecciones, filtroNiveles, filtroTareas, filtroEstados, busqueda]);

  // Fix BM (2026-09-08): el fondo del servidor se calculaba sobre su propio
  // filtro; ahora se suma lo que se está viendo. El Residente no recibe los
  // montos, así que para él el conteo es la referencia.
  const fondoFiltrado = useMemo(
    () => tareasFiltradas.reduce((s, t) => s + (Number(t.fondo_mo) || 0), 0),
    [tareasFiltradas]);
  // Fix IF: precio de venta de lo que se está viendo (vista del cliente).
  const ventaFiltrada = useMemo(
    () => tareasFiltradas.reduce((s, t) => s + (Number(t.precio_venta) || 0), 0),
    [tareasFiltradas]);

  // Fix BO (2026-09-08): "si abajo podemos agregar una fila donde aparezca la suma
  // de las columnas de Fondo M.O. y Saldo de lo que se va filtrando y cuando no hay
  // filtro que deberia ser la suma de todo" (Edson, 08/09). Sin filtros
  // `tareasFiltradas` son todas las tareas, así que el mismo cálculo cubre los dos
  // casos. Se suma también Consumido para que las tres columnas de dinero cierren
  // entre sí: fondo - consumido = saldo.
  const totalesFiltrados = useMemo(() => tareasFiltradas.reduce((acc, t) => ({
    fondo: acc.fondo + (Number(t.fondo_mo) || 0),
    consumido: acc.consumido + (Number(t.consumido_jornal) || 0) + (Number(t.consumido_tercerizado) || 0),
    saldo: acc.saldo + (Number(t.fondo_disponible) || 0),
    material: acc.material + (Number(t.fondo_material) || 0),   // Fix IS
  }), { fondo: 0, consumido: 0, saldo: 0, material: 0 }), [tareasFiltradas]);

  // Fix BZ (2026-09-09): además de agrupar por ítem, se marca dónde cambia la
  // sección para poder poner su encabezado arriba. Se pasa de objeto a array a
  // propósito: el orden que llega del servidor (item_obra_idx, tarea_numero) es
  // el del cómputo, y en un objeto se conservaba solo porque las claves son uid
  // alfanuméricos — con un uid que fuera todo dígitos, JavaScript lo reordena.
  const grupos = useMemo(() => {
    const orden = [];
    const porUid = {};
    tareasFiltradas.forEach(t => {
      if (!porUid[t.item_uid]) { porUid[t.item_uid] = { itemUid: t.item_uid, rows: [] }; orden.push(porUid[t.item_uid]); }
      porUid[t.item_uid].rows.push(t);
    });
    // Fix CA (2026-09-09): el Fix BZ marcaba el arranque de sección comparando
    // con el grupo anterior, dando por sentado que las tareas llegan agrupadas
    // por sección. No llegan: el orden del servidor es (item_obra_idx,
    // tarea_numero) y en la obra los ítems de "3.29 PLANTA TIPO" y "3.44 AZOTEA"
    // están intercalados porque los clones por nivel del Fix BF se insertaron
    // entremedio. Medido en INVICTA HERRERA: 13 secciones distintas y 78 bandas
    // pintadas. Se agrupa de verdad por sección — el orden de las secciones es
    // el de primera aparición (un Map conserva el orden de inserción), que es el
    // del cómputo, y dentro de cada sección los ítems mantienen el orden que ya
    // tenían. No se agrega, borra ni reordena ninguna tarea dentro de su ítem.
    const porSeccion = new Map();
    orden.forEach(g => {
      const seccion = g.rows[0].item_seccion || "";
      if (!porSeccion.has(seccion)) porSeccion.set(seccion, []);
      porSeccion.get(seccion).push(g);
    });
    const salida = [];
    porSeccion.forEach((gruposDeLaSeccion, seccion) => {
      gruposDeLaSeccion.forEach((g, i) => salida.push({
        ...g, seccion, abreSeccion: i === 0, itemsSeccion: gruposDeLaSeccion.length,
      }));
    });
    return salida;
  }, [tareasFiltradas]);

  if (loading && !tareas.length) return <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>;

  return (
    <div className="space-y-4" data-testid="obra-tareas-tab">
      {/* Fix EC (2026-09-24): "Tampoco aparecen los adicionales en … TAREAS". */}
      {gestion && sinTareas.length > 0 && tareas.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="tareas-aviso-sin-tareas">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-amber-800">
              Hay ítems sin tareas: {sinTareas.map(s => `${s.origen} (${s.cantidad})`).join(", ")}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">Se crean con "Generar tareas desde presupuesto". No modifica las tareas que ya existen.</p>
          </div>
          <button onClick={generar} disabled={generando}
            className="flex-shrink-0 flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
            data-testid="tareas-aviso-sin-tareas-btn">
            {generando ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generar ahora
          </button>
        </div>
      )}
      {/* Fix BZ (2026-09-09): "cambié los niveles en el presupuesto y en la obra
          siguen como antes" (Edson, 09/09). Los ítems de la obra son una copia
          congelada de la aprobación y solo se actualizan con el botón; lo que
          faltaba era el aviso. Se avisa y NO se actualiza solo a propósito:
          traer los cambios reescribe niveles y desglose sobre lo ya cargado y
          además ofrece regenerar las tareas, que borra las existentes. */}
      {gestion && (pendienteSync?.pendientes || 0) > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="tareas-aviso-sync">
          <div className="p-2 rounded-xl bg-amber-100 text-amber-600 flex-shrink-0"><RefreshCw size={16} /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-amber-800">
              El presupuesto tiene {pendienteSync.pendientes} ítem{pendienteSync.pendientes !== 1 ? "s" : ""} con cambios que esta obra todavía no tomó
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              {(pendienteSync.niveles_distintos || 0) > 0 ? `${pendienteSync.niveles_distintos} de ellos son de nivel. ` : ""}
              Traelos con "Actualizar desde presupuesto". No modifica el avance cargado ni los montos.
            </p>
          </div>
          <button onClick={resincronizar} disabled={resincronizando}
            className="flex-shrink-0 flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
            data-testid="tareas-aviso-sync-btn">
            {resincronizando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar ahora
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          {/* Fix BM (2026-09-08): los tres filtros con multiselección.
              Fix BZ (2026-09-09): Secciones va primero porque es el nivel más
              alto del cómputo, con el mismo componente y el mismo comportamiento
              que el de Ejecución en Campo. */}
          {opcionesOrigen.length > 1 && (
            <FiltroMulti etiqueta="Presupuesto" opciones={opcionesOrigen} seleccion={filtroOrigen}
              onAplicar={setFiltroOrigen} testId="tareas-filtro-origen" />
          )}
          <FiltroMulti etiqueta="Secciones" opciones={opcionesSeccion} seleccion={filtroSecciones}
            onAplicar={setFiltroSecciones} testId="tareas-filtro-seccion" />
          <FiltroMulti etiqueta="Niveles" opciones={opcionesNivel} seleccion={filtroNiveles}
            onAplicar={setFiltroNiveles} testId="tareas-filtro-nivel" />
          <FiltroMulti etiqueta="Tareas" opciones={opcionesTarea} seleccion={filtroTareas}
            onAplicar={setFiltroTareas} testId="tareas-filtro-nombre" />
          <FiltroMulti etiqueta="Estados" opciones={opcionesEstado} seleccion={filtroEstados}
            onAplicar={setFiltroEstados} testId="tareas-filtro-estado" />
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Filtrar por ítem, tarea o sección..."
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs w-56 self-center"
            data-testid="tareas-buscar" />
          {(filtroOrigen.size > 0 || filtroSecciones.size > 0 || filtroNiveles.size > 0 || filtroTareas.size > 0 || filtroEstados.size > 0 || busqueda) && (
            <button type="button" onClick={() => { setFiltroOrigen(new Set()); setFiltroSecciones(new Set()); setFiltroNiveles(new Set()); setFiltroTareas(new Set()); setFiltroEstados(new Set()); setBusqueda(""); }}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline self-center"
              data-testid="tareas-limpiar-filtros">
              Limpiar filtros
            </button>
          )}
          <span className="text-xs font-semibold text-gray-500 self-center" data-testid="tareas-fondo-filtrado">
            Mostrando {tareasFiltradas.length} de {tareas.length}
            {gestion ? ` · Fondo: ${formatPYG(fondoFiltrado)} · Material: ${formatPYG(totalesFiltrados.material)}` : vistaCliente ? ` · Precio de venta: ${formatPYG(ventaFiltrada)}` : ""}
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowGantt(v => !v)} className="flex items-center gap-1.5 border border-gray-300 hover:border-[#EA580C] text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="tareas-toggle-gantt">
            <BarChart3 size={13} /> {showGantt ? "Ocultar Gantt" : "Ver Gantt"}
          </button>
          {canFullAccess && (
            <button onClick={() => setShowCurva(v => !v)} className="flex items-center gap-1.5 border border-gray-300 hover:border-[#EA580C] text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="tareas-toggle-curva">
              <TrendingUp size={13} /> {showCurva ? "Ocultar Curva S" : "Curva S"}
            </button>
          )}
          <button onClick={openResumen} className="flex items-center gap-1.5 border border-gray-300 hover:border-[#EA580C] text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="tareas-ver-resumen-btn">
            <ListChecks size={13} /> Avance por nivel
          </button>
          {gestion && (
            <button onClick={resincronizar} disabled={resincronizando} className="flex items-center gap-1.5 border border-gray-300 hover:border-[#EA580C] text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid="tareas-resincronizar-btn">
              {resincronizando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar desde presupuesto
            </button>
          )}
          {/* Fix CO (2026-09-13): el contrato con el tercerizado se arma desde acá,
              sobre las tareas que se están viendo, sin pasar por la pestaña Pagos. */}
          {gestion && (
            <button onClick={() => setShowContrato(true)} className="flex items-center gap-1.5 border border-gray-300 hover:border-[#EA580C] text-gray-600 rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="tareas-contrato-tercero-btn">
              <HandCoins size={13} /> Contrato con tercero
            </button>
          )}
          {gestion && (
            <button onClick={generar} disabled={generando} className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid="tareas-generar-btn">
              {generando ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generar tareas desde presupuesto
            </button>
          )}
        </div>
      </div>

      {showGantt && (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm">
          <GanttChart tareas={tareasFiltradas} />
        </div>
      )}
      {showCurva && canFullAccess && (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm">
          <CurvaS tareas={tareasFiltradas} />
        </div>
      )}
      {periodoCopiado && (
        <div className="flex flex-wrap items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-xs" data-testid="tareas-periodo-copiado">
          <span className="text-emerald-900">Período copiado de <b>{periodoCopiado.desde}</b>: {formatDate(periodoCopiado.inicio)} → {formatDate(periodoCopiado.fin)}{periodoCopiado.duracion_dias ? ` (${periodoCopiado.duracion_dias} días)` : ""}</span>
          {!confirmarPegarTodo ? (
            <button type="button" onClick={() => setConfirmarPegarTodo(true)} className="font-semibold text-emerald-800 hover:underline" data-testid="tareas-pegar-filtradas">
              Pegar en las {tareasFiltradas.filter(x => x.id !== periodoCopiado.id).length} tareas filtradas
            </button>
          ) : (
            <span className="flex items-center gap-2">
              <button type="button" onClick={async () => { setConfirmarPegarTodo(false); await pegarPeriodo(tareasFiltradas.filter(x => x.id !== periodoCopiado.id)); }}
                className="px-2 py-1 rounded bg-emerald-600 text-white font-semibold" data-testid="tareas-pegar-filtradas-ok">Confirmar</button>
              <button type="button" onClick={() => setConfirmarPegarTodo(false)} className="px-2 py-1 rounded border border-gray-300 bg-white">Cancelar</button>
            </span>
          )}
          <button type="button" onClick={() => { setPeriodoCopiado(null); setConfirmarPegarTodo(false); }} className="ml-auto text-gray-500 hover:underline" data-testid="tareas-periodo-quitar">Quitar</button>
        </div>
      )}

      {tareas.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm">
          <ListChecks size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-600 font-medium">Todavía no hay tareas generadas</p>
          {gestion && <p className="text-xs text-gray-400 mt-1">Usá "Generar tareas desde presupuesto" para crear la primera tarea de cada ítem.</p>}
        </div>
      ) : (
        <div className="overflow-auto max-h-[70vh] rounded-2xl border border-gray-200/60 bg-white relative" data-testid="tareas-scroll">
          {/* Fix BB (2026-09-07): "En este apartado que quede fijo ese encabezado"
              (Edson, 06/09 19:14). La tabla pasa a tener altura acotada y scroll
              propio, y el encabezado se pega arriba: en una obra con cientos de
              tareas ya no hay que subir hasta el principio para saber qué columna
              se está mirando. El `sticky` va en cada celda del encabezado
              (variante [&_th]) y no solo en el thead, que es lo que lo hace
              funcionar parejo en todos los navegadores. Mismo criterio del fix I
              en el detalle del presupuesto y del fix S en el pedido. */}
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-20 [&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-gray-50 [&_th]:shadow-[inset_0_-1px_0_rgb(243,244,246)]">
              <tr className="bg-gray-50 border-b border-gray-100 text-left text-gray-500 uppercase text-[10px]">
                <th className="px-2 py-2">Ítem / Tarea</th>
                <th className="px-2 py-2">Nivel</th>
                <th className="px-2 py-2 text-center">Of.</th>
                <th className="px-2 py-2 text-center">Med.</th>
                <th className="px-2 py-2 text-center">Ay.</th>
                {gestion && <th className="px-2 py-2 text-right">Fondo M.O.</th>}
                {vistaCliente && <th className="px-2 py-2 text-right">Precio de venta</th>}
                {/* Fix AV (2026-09-06): "a parte del monto inicial disponible, se
                    debe visualizar el saldo de ese fondo" (Edson, 05/09). */}
                {gestion && <th className="px-2 py-2 text-right">Consumido</th>}
                {gestion && <th className="px-2 py-2 text-right">Saldo</th>}
                {/* Fix IS (2026-11-02): "que visualices el fondo de material" (reunión CEN, 30/10). */}
                {gestion && <th className="px-2 py-2 text-right">Fondo material</th>}
                <th className="px-2 py-2">Inicio</th>
                <th className="px-2 py-2 text-center">Días</th>
                <th className="px-2 py-2">Fin</th>
                <th className="px-2 py-2">Estado</th>
                <th className="px-2 py-2 text-center">Terc.</th>
                {!vistaCliente && <th className="px-2 py-2 text-center">% Var</th>}
                <th className="px-2 py-2">Observaciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {grupos.map(({ itemUid, rows, seccion, abreSeccion, itemsSeccion }) => (
                <Fragment key={itemUid}>
                  {/* Fix BZ (2026-09-09): la sección del cómputo, arriba de sus
                      ítems. Con "3.29 PLANTA TIPO" clonada por nivel, dos
                      sub-ítems con la misma descripción solo se distinguen por
                      acá. Mismo texto que muestra Ejecución en Campo. */}
                  {abreSeccion && (
                    <tr className="bg-[#1A4B7A]" data-testid={`tareas-seccion-header-${itemUid}`}>
                      <td colSpan={gestion ? 16 : 12} className="px-2 py-1.5 font-bold text-white uppercase tracking-wide text-[11px]">
                        {/* Fix CA (2026-09-09): con la sección agrupada de verdad,
                            el conteo dice de un vistazo cuánto pesa cada una. */}
                        {seccion || "Sin sección"} · {itemsSeccion} ítem{itemsSeccion !== 1 ? "s" : ""}
                      </td>
                    </tr>
                  )}
                  <tr key={`h-${itemUid}`} className="bg-[#EBF3FB]" data-testid={`tareas-item-header-${itemUid}`}>
                    <td colSpan={gestion ? 16 : 12} className="px-2 py-1.5 font-bold text-[#2E6DA4] flex items-center justify-between">
                      <span>
                        {rows[0].item_numero} — {rows[0].item_descripcion}
                        {rows[0].item_origen && rows[0].item_origen !== "Base" && (
                          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`tareas-origen-${itemUid}`}>
                            {rows[0].item_origen}
                          </span>
                        )}
                      </span>
                      {gestion && (
                        <button onClick={() => setAddModalItem({ uid: itemUid, label: rows[0].item_descripcion })} className="text-[10px] flex items-center gap-1 text-[#2E6DA4] hover:underline" data-testid={`tareas-add-btn-${itemUid}`}>
                          <Plus size={11} /> Tarea
                        </button>
                      )}
                    </td>
                  </tr>
                  {rows.map(t => {
                    const estMeta = ESTADO_META[t.estado] || ESTADO_META.no_iniciado;
                    const pctUso = t.pct_uso_cuadrilla ?? 0;
                    return (
                      <tr key={t.id} className="hover:bg-gray-50/60" data-testid={`tareas-row-${t.id}`}>
                        <td className="px-2 py-2 pl-5 text-gray-700">
                          {t.tarea_nombre}
                          {/* Fix CJ (2026-09-11): informes de la tarea */}
                          <button type="button" onClick={() => setInformesTarea(t)} title="Informes de esta tarea"
                            className={`ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold ${(informesPorTarea[t.id] || []).length ? "text-[#EA580C]" : "text-gray-300 hover:text-gray-500"}`}
                            data-testid={`tareas-informes-${t.id}`}>
                            <Camera size={11} />{(informesPorTarea[t.id] || []).length || ""}
                          </button>
                        </td>
                        <td className="px-2 py-2 text-gray-500">{t.nivel_label || "—"}</td>
                        {["oficiales", "medios", "ayudantes"].map(k => (
                          <td key={k} className="px-2 py-2 text-center">
                            <input type="number" min={0} defaultValue={t.cuadrilla?.[k] || 0}
                              onBlur={e => patchTarea(t.id, { cuadrilla: { ...t.cuadrilla, [k]: Number(e.target.value) || 0 } })}
                              className="w-12 border border-gray-200 rounded px-1 py-0.5 text-center" data-testid={`tareas-cuadrilla-${k}-${t.id}`} />
                          </td>
                        ))}
                        {gestion && <td className="px-2 py-2 text-right font-semibold text-gray-700">{formatPYG(t.fondo_mo || 0)}</td>}
                        {vistaCliente && (
                          <td className="px-2 py-2 text-right font-semibold text-gray-700" data-testid={`tareas-precio-venta-${t.id}`}>
                            {t.precio_venta == null ? "—" : formatPYG(t.precio_venta)}
                          </td>
                        )}
                        {gestion && (
                          <td className="px-2 py-2 text-right text-gray-600" data-testid={`tareas-consumido-${t.id}`}>
                            {formatPYG((t.consumido_jornal || 0) + (t.consumido_tercerizado || 0))}
                            <span className="block text-[9px] text-gray-400">
                              jornal {formatPYG(t.consumido_jornal || 0)}{t.tercerizado ? ` · terc. ${formatPYG(t.consumido_tercerizado || 0)}` : ""}
                            </span>
                          </td>
                        )}
                        {gestion && (
                          <td className={`px-2 py-2 text-right font-bold ${(t.fondo_disponible || 0) < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={`tareas-saldo-${t.id}`}>
                            {formatPYG(t.fondo_disponible || 0)}
                          </td>
                        )}
                        {gestion && (
                          <td className="px-2 py-2 text-right" data-testid={`tareas-fondo-material-${t.id}`}>
                            {/* Fix IS: costo de material del ítem × % de material de la tarea (sin % propio, el de M.O.). */}
                            <span className="font-semibold text-gray-700" data-testid={`tareas-fondo-material-monto-${t.id}`}>{t.fondo_material == null ? "—" : formatPYG(t.fondo_material)}</span>
                            <label className="flex items-center justify-end gap-1 text-[9px] text-gray-400 mt-0.5 whitespace-nowrap">
                              <input type="number" min={0} max={100} step="any" key={`pm-${t.pct_material_efectivo ?? ""}`} defaultValue={t.pct_material_efectivo ?? ""}
                                onBlur={e => {
                                  const el = e.target;
                                  if (el.value === "" || Number(el.value) === Number(t.pct_material_efectivo)) { el.value = t.pct_material_efectivo ?? ""; return; }
                                  patchTarea(t.id, { pct_material: Number(el.value) }).then(r => { if (!r) el.value = t.pct_material_efectivo ?? ""; });
                                }}
                                className="w-12 border border-gray-200 rounded px-1 py-0.5 text-right text-[10px] text-gray-600" data-testid={`tareas-pct-material-${t.id}`} />
                              % mat.
                            </label>
                          </td>
                        )}
                        <td className="px-2 py-2">
                          {/* Fix AW (2026-09-06): al cargar inicio o días, el fin se calcula solo. */}
                          <input type="date" key={`i-${t.plan?.inicio || ""}`} defaultValue={t.plan?.inicio || ""}
                            onBlur={e => {
                              const inicio = e.target.value || null;
                              if (inicio && t.plan?.duracion_dias) calcularFin(t, inicio, t.plan.duracion_dias, false);
                              else guardarPlan(t, { ...t.plan, inicio });
                            }}
                            className="border border-gray-200 rounded px-1 py-0.5 w-28" data-testid={`tareas-plan-inicio-${t.id}`} />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input type="number" min={0} key={`d-${t.plan?.duracion_dias || ""}`} defaultValue={t.plan?.duracion_dias || ""}
                            onBlur={e => {
                              const dias = Number(e.target.value) || null;
                              if (dias && t.plan?.inicio) calcularFin(t, t.plan.inicio, dias, false);
                              else guardarPlan(t, { ...t.plan, duracion_dias: dias });
                            }}
                            className="w-14 border border-gray-200 rounded px-1 py-0.5 text-center" data-testid={`tareas-plan-dias-${t.id}`} />
                        </td>
                        <td className="px-2 py-2">
                          <input type="date" key={`f-${t.plan?.fin || ""}`} defaultValue={t.plan?.fin || ""} onBlur={e => guardarPlan(t, { ...t.plan, fin: e.target.value || null })} className="border border-gray-200 rounded px-1 py-0.5 w-28" data-testid={`tareas-plan-fin-${t.id}`} />
                          <div className="flex gap-2 mt-0.5">
                            {t.plan?.inicio && t.plan?.fin && (
                              <button type="button" onClick={() => copiarPeriodo(t)} className="text-[10px] font-semibold text-[#2E6DA4] hover:underline" data-testid={`tareas-copiar-periodo-${t.id}`}>Copiar período</button>
                            )}
                            {periodoCopiado && periodoCopiado.id !== t.id && (
                              <button type="button" onClick={() => pegarPeriodo([t])} className="text-[10px] font-semibold text-emerald-700 hover:underline" data-testid={`tareas-pegar-periodo-${t.id}`}>Pegar</button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <select defaultValue={t.estado} onChange={e => patchTarea(t.id, { estado: e.target.value })} className={`text-[10px] font-bold px-1.5 py-1 rounded-full border-none ${estMeta.cls}`} data-testid={`tareas-estado-${t.id}`}>
                            <option value="no_iniciado">No iniciado</option>
                            <option value="en_proceso">En proceso</option>
                            <option value="finalizado">Finalizado</option>
                          </select>
                        </td>
                        <td className="px-2 py-2 text-center">
                          {/* Fix DX (2026-09-23): controlado (si el backend rechaza el
                              cambio, el tilde vuelve a su lugar) y fijo si la tarea está
                              en un contrato con terceros: su consumo sale de los avances
                              validados del contrato y no se toca desde la planilla. */}
                          <input type="checkbox" checked={!!t.tercerizado} disabled={!gestion || !!t.contrato_tercero}
                            onChange={e => patchTarea(t.id, { tercerizado: e.target.checked })} data-testid={`tareas-tercerizado-${t.id}`} />
                          {t.contrato_tercero && (
                            <p className="mt-1 w-32 text-[10px] text-gray-500 text-left" data-testid={`tareas-en-contrato-${t.id}`}>
                              Contrato: <span className="font-semibold text-gray-700">{t.contrato_tercero}</span>
                            </p>
                          )}
                          {/* Fix AV (2026-09-06): con la tarea tercerizada se cargan
                              el contratista y el monto cerrado, y se ve lo cobrado. */}
                          {gestion && t.tercerizado && !t.contrato_tercero && (
                            <div className="mt-1 space-y-1 text-left">
                              <input defaultValue={t.contratista || ""} placeholder="Contratista"
                                onBlur={e => patchTarea(t.id, { contratista: e.target.value })}
                                className="w-32 border border-gray-200 rounded px-1 py-0.5 text-[10px]" data-testid={`tareas-contratista-${t.id}`} />
                              {/* Fix CO (2026-09-13): puntos de miles al tipear y el
                                  fondo disponible de la tarea debajo, para no cerrar
                                  un monto por encima del fondo sin darse cuenta. */}
                              <MontoInput value={t.monto_cerrado_tercerizado || 0}
                                onCommit={v => patchTarea(t.id, { monto_cerrado_tercerizado: v })}
                                placeholder="Monto cerrado"
                                className={`w-32 border rounded px-1 py-0.5 text-[10px] ${(Number(t.monto_cerrado_tercerizado) || 0) > (Number(t.fondo_mo) || 0) + 0.01 ? "border-red-300 bg-red-50/40" : "border-gray-200"}`}
                                data-testid={`tareas-monto-cerrado-${t.id}`} />
                              <p className="w-32 text-[9px] text-gray-400 text-right" data-testid={`tareas-disponible-terc-${t.id}`}>
                                Disponible {formatPYG(t.fondo_disponible || 0)}
                              </p>
                              <button type="button" onClick={() => setPagosTarea(t)}
                                className="w-32 text-[10px] font-semibold text-[#2E6DA4] hover:underline text-right"
                                data-testid={`tareas-cobrado-${t.id}`}>
                                Cobrado: {formatPYG(t.cobrado_tercerizado || 0)}
                                <span className="block text-[9px] text-gray-400 font-normal">falta {formatPYG(t.saldo_contratista || 0)}</span>
                              </button>
                            </div>
                          )}
                        </td>
                        {!vistaCliente && (
                          <td className="px-2 py-2 text-center">
                            <span className={`font-bold px-1.5 py-0.5 rounded-full ${pctUso > 100 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`tareas-pct-uso-${t.id}`}>{pctUso}%</span>
                          </td>
                        )}
                        <td className="px-2 py-2">
                          <input defaultValue={t.observaciones || ""} onBlur={e => patchTarea(t.id, { observaciones: e.target.value })} className="w-full border border-gray-200 rounded px-1 py-0.5" data-testid={`tareas-obs-${t.id}`} />
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
            {/* Fix BO (2026-09-08): totales de lo que se está viendo, pegados abajo
                para que sigan a la vista mientras se recorre la lista. Solo con
                acceso a montos: el Residente no recibe esos campos del backend y
                estas tres columnas no existen en su tabla. */}
            {gestion && (
              <tfoot className="sticky bottom-0 z-20 [&_td]:sticky [&_td]:bottom-0 [&_td]:z-20 [&_td]:bg-[#FFF7ED] [&_td]:shadow-[inset_0_1px_0_rgb(229,231,235)]">
                <tr className="text-[11px] font-bold text-gray-700" data-testid="tareas-totales">
                  <td className="px-2 py-2 text-right" colSpan={5}>
                    Totales de lo filtrado ({tareasFiltradas.length} de {tareas.length})
                  </td>
                  <td className="px-2 py-2 text-right" data-testid="tareas-total-fondo">{formatPYG(totalesFiltrados.fondo)}</td>
                  <td className="px-2 py-2 text-right text-gray-600" data-testid="tareas-total-consumido">{formatPYG(totalesFiltrados.consumido)}</td>
                  <td className={`px-2 py-2 text-right ${totalesFiltrados.saldo < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="tareas-total-saldo">{formatPYG(totalesFiltrados.saldo)}</td>
                  <td className="px-2 py-2 text-right" data-testid="tareas-total-material">{formatPYG(totalesFiltrados.material)}</td>
                  <td className="px-2 py-2" colSpan={7} />
                </tr>
              </tfoot>
            )}
            {/* Fix IF: en la vista del cliente, el total del precio de venta de lo filtrado. */}
            {vistaCliente && (
              <tfoot className="sticky bottom-0 z-20 [&_td]:sticky [&_td]:bottom-0 [&_td]:z-20 [&_td]:bg-[#EBF3FB] [&_td]:shadow-[inset_0_1px_0_rgb(229,231,235)]">
                <tr className="text-[11px] font-bold text-gray-700" data-testid="tareas-totales-venta">
                  <td className="px-2 py-2 text-right" colSpan={5}>
                    <Eye size={12} className="inline mr-1 text-[#2E6DA4]" />Precio de venta de lo filtrado ({tareasFiltradas.length} de {tareas.length})
                  </td>
                  <td className="px-2 py-2 text-right" data-testid="tareas-total-venta">{formatPYG(ventaFiltrada)}</td>
                  <td className="px-2 py-2" colSpan={6} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {informesTarea && (
        <InformesTareaModal obraId={obraId} tarea={informesTarea} informes={informesPorTarea[informesTarea.id] || []}
          tareas={tareas} onClose={() => setInformesTarea(null)} onSaved={cargarInformes} />
      )}

      {/* Fix IJ (2026-10-30): la misma tarea en el mismo nivel — se pregunta si se le aplica el mismo período. */}
      {mismoNivel && createPortal(
        <div className="fixed inset-0 z-[9991] flex items-center justify-center p-4" data-testid="tareas-mismo-nivel-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMismoNivel(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-sm font-bold text-gray-900">¿El mismo período para las tareas iguales del nivel?</h3>
            <p className="text-xs text-gray-600">
              Hay {mismoNivel.otras.length} tarea(s) «{mismoNivel.tarea.tarea_nombre}» más en el nivel <b>{mismoNivel.tarea.nivel_label}</b>.
              ¿Les aplicamos {formatDate(mismoNivel.plan.inicio)} → {formatDate(mismoNivel.plan.fin)}?
            </p>
            <ul className="text-[11px] text-gray-600 bg-gray-50 rounded-lg p-2 max-h-32 overflow-y-auto space-y-0.5">
              {mismoNivel.otras.map(x => <li key={x.id}>{x.item_numero} — {x.item_descripcion}</li>)}
            </ul>
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setMismoNivel(null)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600" data-testid="tareas-mismo-nivel-no">No, solo esta</button>
              <button onClick={async () => { const m = mismoNivel; setMismoNivel(null); const n = await aplicarPeriodo(m.otras, m.plan); if (n) toast.success(`Período aplicado a ${n} tarea(s) del nivel ${m.tarea.nivel_label}`); }}
                className="px-3 py-2 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg text-xs font-semibold" data-testid="tareas-mismo-nivel-aplicar">Aplicar a las {mismoNivel.otras.length}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Fix AW (2026-09-06): el tramo pisa días no laborables — se pregunta. */}
      {confirmFin && createPortal(
        <div className="fixed inset-0 z-[9991] flex items-center justify-center p-4" data-testid="confirm-fin-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmFin(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-sm font-bold text-gray-900">Días no laborables en el plazo</h3>
            <p className="text-xs text-gray-600">
              Entre el {confirmFin.inicio} y el fin del plazo hay {confirmFin.data.dias_no_laborables.length} día(s) que por defecto no se trabajan:
            </p>
            <ul className="text-[11px] text-gray-600 bg-gray-50 rounded-lg p-2 max-h-32 overflow-y-auto space-y-0.5">
              {confirmFin.data.dias_no_laborables.map(d => (
                <li key={d.fecha}>{d.fecha} — {d.motivo}</li>
              ))}
            </ul>
            <p className="text-xs text-gray-600">
              Sin trabajar esos días, la tarea termina el <strong>{confirmFin.data.fin}</strong>. Si se trabajan, termina el <strong>{confirmFin.data.corrido}</strong>.
            </p>
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={() => setConfirmFin(null)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">Cancelar</button>
              <button onClick={async () => { const c = confirmFin; setConfirmFin(null); await guardarPlan(c.tarea, { ...c.tarea.plan, inicio: c.inicio, duracion_dias: c.dias, fin: c.data.corrido }); }}
                className="px-3 py-2 border border-[#2E6DA4] text-[#2E6DA4] rounded-lg text-xs font-semibold" data-testid="confirm-fin-si-trabaja">
                Sí se trabajan
              </button>
              <button onClick={async () => { const c = confirmFin; setConfirmFin(null); await guardarPlan(c.tarea, { ...c.tarea.plan, inicio: c.inicio, duracion_dias: c.dias, fin: c.data.fin }); }}
                className="px-3 py-2 bg-[#2E6DA4] text-white rounded-lg text-xs font-bold" data-testid="confirm-fin-no-trabaja">
                No se trabajan
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {pagosTarea && (
        <PagosTercerizadoModal
          obraId={obraId}
          tarea={pagosTarea}
          onClose={() => setPagosTarea(null)}
          onSaved={(actualizada) => { setPagosTarea(actualizada); load(); }}
        />
      )}
      {showContrato && (
        <ContratoTerceroModal
          obraId={obraId}
          tareas={tareasFiltradas}
          preseleccion={tareasFiltradas.filter(t => t.tercerizado && !t.contrato_tercero).map(t => t.id)}
          onClose={() => setShowContrato(false)}
          onSaved={load}
        />
      )}
      {addModalItem && (
        <AddTareaModal itemUid={addModalItem.uid} itemLabel={addModalItem.label} onClose={() => setAddModalItem(null)} onSaved={addTarea} />
      )}

      {showResumen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowResumen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6" data-testid="tareas-resumen-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Avance por Nivel</h2>
              <button onClick={() => setShowResumen(false)}><X size={18} className="text-gray-400" /></button>
            </div>
            {!resumen ? <Loader2 className="animate-spin mx-auto text-gray-400" /> : resumen.rows.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Sin tareas todavía.</p>
            ) : (
              /* Fix N (2026-08-31): grilla niveles × tareas, como la pidió el cliente
                 en el documento de acotaciones. Antes eran etiquetas sueltas por nivel,
                 así que no se podía comparar la misma tarea entre pisos, que es
                 justamente para lo que sirve. El backend ya devolvía la matriz. */
              (() => {
                const columnas = [...new Set(resumen.rows.flatMap(r => Object.keys(r.tareas || {})))].sort();
                const color = (p) => p >= 100 ? "bg-emerald-100 text-emerald-700" : p > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-50 text-gray-400";
                return (
                  <div className="overflow-auto max-h-[60vh] border border-gray-200 rounded-xl relative" data-testid="tareas-matriz">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-600 bg-white border-b border-gray-200 sticky left-0 z-20">Nivel</th>
                          <th className="px-2 py-2 text-center font-semibold text-gray-600 bg-white border-b border-gray-200">Avance</th>
                          {columnas.map(tn => (
                            <th key={tn} className="px-2 py-2 text-center font-medium text-gray-500 bg-white border-b border-gray-200 whitespace-nowrap max-w-[140px] truncate" title={tn}>{tn}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {resumen.rows.map((r, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="px-3 py-2 font-semibold text-gray-800 bg-white sticky left-0">{r.nivel}</td>
                            <td className="px-2 py-2 text-center font-bold text-[#EA580C]">{r.pct_nivel}%</td>
                            {columnas.map(tn => {
                              const c = r.tareas?.[tn];
                              return (
                                <td key={tn} className="px-2 py-2 text-center">
                                  {c ? <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${color(c.pct)}`} title={`${c.finalizadas} de ${c.total} finalizadas`}>{c.pct}%</span> : <span className="text-gray-200">·</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>
        </div>, document.body
      )}
    </div>
  );
}
